import { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Typography } from '@sunrun/experience-ui-components';
import { InfoIconPopover } from 'components/atoms/InfoIconPopover';
import { ON_INFO_SURFACE } from 'components/atoms/InfoPopover';
import {
  getPerformanceScopesForRole,
  resolvePerformanceScopeSelection,
  type PerformanceData,
  type PerformanceScopeKey,
  type PerformanceTripleMetric,
} from 'types/performance';
import { PerformanceWidgetSelect } from 'components/atoms/PerformanceWidgetSelect';
import { formatDisplayNumber } from 'helpers/formatHelpers';
import { NewDashboardOrgType } from 'helpers/repTypeHelper';
import { METRIC_INFO_BODIES } from 'constants/metricInfoBodies';

const NAVY = '#181e3a';
const MUTED = '#5c6378';
const TRACK_BG = '#e5e7eb';
const CARD_BG = 'rgba(0, 0, 0, 0.04)';

const PACE_GREEN_GRADIENT =
  'linear-gradient(90deg, rgba(26, 110, 92, 1), rgba(35, 165, 137, 1))';
const PACE_GREEN_ACCENT = 'rgba(26, 110, 92, 1)';

const PACE_YELLOW_GRADIENT =
  'linear-gradient(90deg, rgba(184, 141, 39, 1), rgba(236, 181, 51, 1))';
const PACE_YELLOW_ACCENT = 'rgba(184, 141, 39, 1)';

const PACE_RED_GRADIENT =
  'linear-gradient(90deg, rgba(171, 53, 67, 1), rgba(255, 82, 102, 1))';
const PACE_RED_ACCENT = 'rgba(171, 53, 67, 1)';

function barColorsForPaceVsGoal(metric: PerformanceTripleMetric): {
  fill: string;
  accent: string;
  valueColor: string;
} {
  const goal = metric.goal > 0 ? metric.goal : 0;
  const pace = metric.pace > 0 ? metric.pace : 0;
  if (goal <= 0 || pace <= 0) {
    return {
      fill: CARD_BG,
      accent: NAVY,
      valueColor: NAVY,
    };
  }
  const pctOfGoal = (pace / goal) * 100;
  if (pctOfGoal >= 100) {
    return {
      fill: PACE_GREEN_GRADIENT,
      accent: PACE_GREEN_ACCENT,
      valueColor: '#ffffff',
    };
  }
  if (pctOfGoal >= 90) {
    return {
      fill: PACE_YELLOW_GRADIENT,
      accent: PACE_YELLOW_ACCENT,
      valueColor: '#ffffff',
    };
  }
  return {
    fill: PACE_RED_GRADIENT,
    accent: PACE_RED_ACCENT,
    valueColor: '#ffffff',
  };
}

type SummaryPeriod = 'current' | 'last';
type ScopeKey = PerformanceScopeKey;
type TimeBucket = 'week' | 'month' | 'quarter';

const SCOPE_OPTIONS: { label: string; value: ScopeKey }[] = [
  { label: 'Me', value: 'me' },
  { label: 'Team', value: 'team' },
  { label: 'Region', value: 'region' },
  { label: 'Coast', value: 'coast' },
  { label: 'All', value: 'all' },
];

const BUCKET_OPTIONS: { label: string; value: TimeBucket }[] = [
  { label: 'Week', value: 'week' },
  { label: 'Month', value: 'month' },
  { label: 'Quarter', value: 'quarter' },
];

function fillScaleMax(m: PerformanceTripleMetric): number {
  if (m.goal > 0) {
    return m.goal;
  }
  if (m.pace > 0) {
    return m.pace;
  }
  return 1;
}

function paceMarkerPct(m: PerformanceTripleMetric): number {
  const goal = m.goal > 0 ? m.goal : 0;
  const pace = m.pace > 0 ? m.pace : 0;
  if (pace <= 0) {
    return 0;
  }
  if (goal > 0) {
    if (pace > goal) {
      return 100;
    }
    return Math.min(100, (pace / goal) * 100);
  }
  return 100;
}

function goalLine(
  m: PerformanceTripleMetric,
): { pct: number; label: string } | null {
  if (m.goal <= 0) {
    return null;
  }
  const pct = Math.round((m.pace / m.goal) * 100);
  return {
    pct,
    label: `Pacing ${pct}% to goal of ${formatDisplayNumber(m.goal)}`,
  };
}

const PerformanceSummary = ({
  data,
  orgType,
}: {
  data: PerformanceData;
  orgType: NewDashboardOrgType;
}) => {
  const allowedScopes = useMemo(
    () => getPerformanceScopesForRole(data.user_config?.role),
    [data.user_config?.role],
  );

  const scopeOptions = useMemo(
    () => SCOPE_OPTIONS.filter((o) => allowedScopes.includes(o.value)),
    [allowedScopes],
  );

  const [summaryPeriod, setSummaryPeriod] = useState<SummaryPeriod>('current');
  const [scope, setScope] = useState<ScopeKey>(() =>
    resolvePerformanceScopeSelection(
      data.user_config,
      getPerformanceScopesForRole(data.user_config?.role),
    ),
  );
  const [timeBucket, setTimeBucket] = useState<TimeBucket>('week');

  useEffect(() => {
    if (!allowedScopes.includes(scope)) {
      setScope(
        resolvePerformanceScopeSelection(data.user_config, allowedScopes),
      );
    }
  }, [allowedScopes, scope, data.user_config]);

  const row = useMemo(() => {
    const scopeData = data[scope];
    if (!scopeData) {
      return null;
    }

    const bucket =
      summaryPeriod === 'current'
        ? scopeData.current[timeBucket]
        : scopeData.last[timeBucket];
    return bucket?.[0] ?? null;
  }, [data, scope, summaryPeriod, timeBucket]);

  const metrics = row?.metrics ?? null;
  const pitches = metrics?.Pitches?.value ?? 0;
  const sales = metrics?.Sales ?? { goal: 0, pace: 0, value: 0 };
  const close = metrics?.Close ?? { goal: 0, pace: 0, value: 0 };
  const install = metrics?.Install ?? { goal: 0, pace: 0, value: 0 };
  const leads = metrics?.Leads?.value ?? 0;
  const accounts = metrics?.Accounts?.value ?? 0;
  const sits = metrics?.SIT?.value ?? 0;
  const oppty = metrics?.Oppty?.value ?? 0;
  const sit = metrics?.Sit?.value ?? 0;
  const sgrSales = metrics?.['SRA (SGR)']?.value ?? 0;
  const isMeScope = scope === 'me';
  const isChain = orgType === 'CHAIN';

  const tripleMetrics = [
    {
      name: 'Sales',
      metric: sales,
      infoBody: METRIC_INFO_BODIES.SRA,
    },
    {
      name: 'Close',
      metric: close,
      infoBody: METRIC_INFO_BODIES.CAP,
    },
    {
      name: 'Install',
      metric: install,
      infoBody: METRIC_INFO_BODIES.IC,
    },
  ] as const;

  return (
    <Root>
      <HeaderRow>
        <Tabs>
          <Tab
            type="button"
            $active={summaryPeriod === 'current'}
            onClick={() => setSummaryPeriod('current')}
            aria-pressed={summaryPeriod === 'current'}>
            Current
          </Tab>
          <Tab
            type="button"
            $active={summaryPeriod === 'last'}
            onClick={() => setSummaryPeriod('last')}
            aria-pressed={summaryPeriod === 'last'}>
            Last
          </Tab>
        </Tabs>
        <Filters>
          <PerformanceWidgetSelect
            options={scopeOptions.map((o) => ({
              label: o.label,
              value: o.value,
            }))}
            value={scope}
            onChange={(e) => setScope(e.target.value as ScopeKey)}
            selectType="button"
            buttonProps={{
              color: 'tertiary',
              size: 'sm',
            }}
          />
          <PerformanceWidgetSelect
            data-testid="sales-ex-dashboard--performance-summary--timeframe-select"
            options={BUCKET_OPTIONS.map((o) => ({
              label: o.label,
              value: o.value,
            }))}
            value={timeBucket}
            onChange={(e) => setTimeBucket(e.target.value as TimeBucket)}
            selectType="button"
            buttonProps={{
              color: 'tertiary',
              size: 'sm',
            }}
          />
        </Filters>
      </HeaderRow>
      <FlexRow>
        {isChain ? (
          <>
            <ValueOnlyCard data-testid="sales-ex-dashboard--performance-summary--opportunity-card">
              <LabelWithInfo>
                <Typography fontWeight="medium">Opportunity</Typography>
              </LabelWithInfo>
              <CardValue>{formatDisplayNumber(oppty)}</CardValue>
            </ValueOnlyCard>
            <ValueOnlyCard data-testid="sales-ex-dashboard--performance-summary--sits-card">
              <LabelWithInfo>
                <Typography fontWeight="medium">Sits</Typography>
              </LabelWithInfo>
              <CardValue>{formatDisplayNumber(sit)}</CardValue>
            </ValueOnlyCard>
            <ValueOnlyCard data-testid="sales-ex-dashboard--performance-summary--sgr-sales-card">
              <LabelWithInfo>
                <Typography fontWeight="medium">SGR Sales</Typography>
              </LabelWithInfo>
              <CardValue>{formatDisplayNumber(sgrSales)}</CardValue>
            </ValueOnlyCard>
          </>
        ) : (
          <ValueOnlyCard data-testid="sales-ex-dashboard--performance-summary--pitches-card">
            <LabelWithInfo>
              <Typography fontWeight="medium">Pitches</Typography>
              <InfoIconPopover
                content={
                  <Typography
                    variant="span"
                    size="small"
                    color={ON_INFO_SURFACE}>
                    Pitches are based on you marking a Terros diposition as a
                    pitch.
                  </Typography>
                }
                contentAriaLabel="Pitches metric"
                contentTestId="sales-ex-dashboard--performance-summary--pitches-info"
                triggerAriaLabel="What Pitches means"
                iconColor={MUTED}
                iconSize={16}
              />
            </LabelWithInfo>
            <CardValue>{formatDisplayNumber(pitches)}</CardValue>
          </ValueOnlyCard>
        )}
        {orgType === 'BLOCK' && (
          <>
            <ValueOnlyCard>
              <LabelWithInfo>
                <Typography fontWeight="medium">Leads</Typography>
              </LabelWithInfo>
              <CardValue>{formatDisplayNumber(leads)}</CardValue>
            </ValueOnlyCard>
            <ValueOnlyCard>
              <LabelWithInfo>
                <Typography fontWeight="medium">Sits</Typography>
              </LabelWithInfo>
              <CardValue>{formatDisplayNumber(sits)}</CardValue>
            </ValueOnlyCard>
          </>
        )}
        {orgType === 'STREET' && (
          <ValueOnlyCard data-testid="sales-ex-dashboard--performance-summary--accounts-card">
            <LabelWithInfo>
              <Typography fontWeight="medium">Accounts</Typography>
              <InfoIconPopover
                content={
                  <Typography
                    variant="span"
                    size="small"
                    color={ON_INFO_SURFACE}>
                    An opportunity created with a utility bill uploaded (Sales
                    leaders are working on this definition, this number may not
                    match your other reports)
                  </Typography>
                }
                contentAriaLabel="Accounts metric"
                contentTestId="sales-ex-dashboard--performance-summary--accounts-info"
                triggerAriaLabel="What Accounts means"
                iconColor={MUTED}
                iconSize={16}
              />
            </LabelWithInfo>
            <CardValue>{formatDisplayNumber(accounts)}</CardValue>
          </ValueOnlyCard>
        )}
        {isMeScope &&
          tripleMetrics.map(({ name, metric, infoBody }) => (
            <TripleMetricValueCard
              key={name}
              name={name}
              value={metric.value}
              infoBody={infoBody}
            />
          ))}
      </FlexRow>
      {!isMeScope && (
        <Rows>
          {tripleMetrics.map(({ name, metric, infoBody }) => (
            <TripleMetricRow
              key={name}
              name={name}
              metric={metric}
              infoBody={infoBody}
            />
          ))}
        </Rows>
      )}
    </Root>
  );
};

const TripleMetricValueCard = ({
  name,
  value,
  infoBody,
}: {
  name: string;
  value: number;
  infoBody: string;
}) => (
  <ValueOnlyCard>
    <LabelWithInfo>
      <Typography fontWeight="medium">{name}</Typography>
      <InfoIconPopover
        content={
          <Typography variant="span" size="small" color={ON_INFO_SURFACE}>
            {infoBody}
          </Typography>
        }
        contentAriaLabel={`${name} metric`}
        contentTestId={`sales-ex-dashboard--performance-summary--${name.toLowerCase()}-info`}
        triggerAriaLabel={`What ${name} means`}
        iconColor={MUTED}
        iconSize={16}
      />
    </LabelWithInfo>
    <CardValue>{formatDisplayNumber(value)}</CardValue>
  </ValueOnlyCard>
);

const TripleMetricRow = ({
  name,
  metric,
  infoBody,
}: {
  name: string;
  metric: PerformanceTripleMetric;
  infoBody?: string;
}) => {
  const { fill: barFill, accent, valueColor } = barColorsForPaceVsGoal(metric);
  const fillMax = fillScaleMax(metric);
  const fillPct = Math.min(100, (metric.value / fillMax) * 100);
  const pacePct = paceMarkerPct(metric);
  const gl = goalLine(metric);

  return (
    <MetricRow
      data-testid={`sales-ex-dashboard--performance-summary--${name.toLowerCase()}-row`}>
      <RowHeader>
        <RowTitleGroup>
          <RowTitle>{name}</RowTitle>
          {infoBody ? (
            <InfoIconPopover
              content={
                <Typography variant="span" size="small" color={ON_INFO_SURFACE}>
                  {infoBody}
                </Typography>
              }
              contentAriaLabel={`${name} metric`}
              contentTestId={`sales-ex-dashboard--performance-summary--${name.toLowerCase()}-info`}
              triggerAriaLabel={`What ${name} means`}
              iconColor={MUTED}
              iconSize={16}
            />
          ) : null}
        </RowTitleGroup>
        <RowGoalMeta>{gl ? gl.label : <>&nbsp;</>}</RowGoalMeta>
      </RowHeader>
      <BarWrap>
        <BarTrack role="presentation" aria-hidden>
          <BarFill
            $fill={barFill}
            style={{ width: `${fillPct}%` }}
            $narrow={fillPct < 12}>
            <BarFillValue $color={valueColor}>
              {formatDisplayNumber(metric.value)}
            </BarFillValue>
          </BarFill>
        </BarTrack>
        <PaceMarker $color={accent} style={{ left: `${pacePct}%` }} />
      </BarWrap>
      {metric.pace > 0 && (
        <PaceFootWrap>
          <PaceFoot
            $color={accent}
            style={
              pacePct > 20
                ? { left: `${pacePct}%` }
                : { transform: 'translateX(0%)' }
            }>
            Pace {formatDisplayNumber(metric.pace)}
          </PaceFoot>
        </PaceFootWrap>
      )}
    </MetricRow>
  );
};

const VALUE_CARD_GAP = '16px';
const VALUE_CARD_MIN_WIDTH = '190px';
const TWO_COL_MIN = `calc(2 * ${VALUE_CARD_MIN_WIDTH} + ${VALUE_CARD_GAP})`;
const THREE_COL_MIN = `calc(3 * ${VALUE_CARD_MIN_WIDTH} + 2 * ${VALUE_CARD_GAP})`;

const FlexRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${VALUE_CARD_GAP};

  @container (min-width: ${THREE_COL_MIN}) {
    &:has(> :nth-child(3)) > * {
      flex: 1 1 calc((100% - 2 * ${VALUE_CARD_GAP}) / 3);
      max-width: calc((100% - 2 * ${VALUE_CARD_GAP}) / 3);
    }
  }
`;

const Root = styled.section`
  container-type: inline-size;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 4px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const Tabs = styled.div`
  display: flex;
  gap: 20px;
`;

const Tab = styled.button<{ $active: boolean }>`
  background: none;
  border: none;
  padding: 0 0 6px;
  cursor: pointer;
  font-size: 15px;
  font-weight: ${(p) => (p.$active ? 600 : 400)};
  color: ${NAVY};
  border-bottom: ${(p) =>
    p.$active ? `3px solid ${NAVY}` : '3px solid transparent'};
  &:focus-visible {
    outline: 2px solid ${NAVY};
    outline-offset: 2px;
  }
`;

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

const ValueOnlyCard = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 12px;
  background: ${CARD_BG};
  box-sizing: border-box;
  flex: 1 1 calc((100% - ${VALUE_CARD_GAP}) / 2);
  max-width: calc((100% - ${VALUE_CARD_GAP}) / 2);
  min-width: ${VALUE_CARD_MIN_WIDTH};

  @container (max-width: calc(${TWO_COL_MIN} - 1px)) {
    flex: 1 1 100%;
    max-width: 100%;
    min-width: 0;
  }
`;

const CardValue = styled.span`
  white-space: nowrap;
  font-size: 20px;
  font-weight: 700;
  color: ${NAVY};
  line-height: 1.2;
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const MetricRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const RowHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const LabelWithInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  > *:last-child {
    flex-shrink: 0;
  }
`;

const RowTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
`;

const RowTitle = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${NAVY};
`;

const RowGoalMeta = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${MUTED};
  text-align: right;
`;

const BarWrap = styled.div`
  position: relative;
  width: 100%;
  height: 28px;
`;

const BarTrack = styled.div`
  position: relative;
  height: 100%;
  border-radius: 4px;
  background: ${TRACK_BG};
  overflow: hidden;
`;

const BarFillValue = styled.span<{ $color: string }>`
  font-size: 14px;
  font-weight: 700;
  color: ${(p) => p.$color};
`;

const BarFill = styled.div<{
  $fill: string;
  $narrow: boolean;
}>`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  min-width: ${(p) => (p.$narrow ? '28px' : '0')};
  border-radius: 4px;
  background: ${(p) => p.$fill};
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-left: 10px;
  box-sizing: border-box;
`;

const PaceMarker = styled.div<{ $color: string }>`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  left: 0;
  transform: translateX(-50%);
  background: ${(p) => p.$color};
  border-radius: 1px;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.35);
  pointer-events: none;
  z-index: 2;
`;

const PaceFootWrap = styled.div`
  position: relative;
  width: 100%;
  min-height: 16px;
`;

const PaceFoot = styled.div<{ $color: string }>`
  position: absolute;
  top: 0;
  left: 0;
  transform: translateX(-100%);
  font-size: 12px;
  font-weight: 600;
  color: ${(p) => p.$color};
  white-space: nowrap;
`;

export { PerformanceSummary };
